import { PageHeader, Selector } from "@ppb/the-wall-web";
import type { SelectorItem } from "@ppb/the-wall-common/types";
import type { FunctionComponent } from "react";
import { memo, useState } from "react";
import type { EventSwitcherItem } from "../viewmodel/EventSwitcherCard.viewmodel";
import { useEventSwitcherCardVM } from "../viewmodel/EventSwitcherCard.viewmodel";
import SELECTORS from "./EventSwitcherCard.selectors";
import styles from "./EventSwitcherCard.web.module.css";

export type EventSwitcherCardProps = {
  viewUrn: string;
  currentViewUrn: string;
  currentLabel: string;
  siblings: EventSwitcherItem[];
  headerTheming?: string | null;
  // In-place switch, the host's call (RaceSwitcherCard parity): when passed, selection swaps the host's
  // active urn instead of emitting `@@UI/GENERIC_SWITCHER_CLOSE`. Native's EventView passes it; web's
  // still navigates on the event — see `event-view-apollo-header-migration.md` §13.4.8.
  onSiblingSelected?: (viewLink: { viewUrn: string; viewUrl: string }) => void;
};

const EventSwitcherCard: FunctionComponent<EventSwitcherCardProps> = ({
  viewUrn,
  currentViewUrn,
  currentLabel,
  siblings,
  headerTheming,
  onSiblingSelected,
}) => {
  const {
    vm: { data, events },
  } = useEventSwitcherCardVM(currentViewUrn, currentLabel, siblings);

  const [selectedItemId, setSelectedItemId] = useState(data.defaultValue.id);

  // Resync when the route re-renders with a new urn while this stays mounted (selection, browser
  // back, deep link). Without it the guard in `onClose` silently drops a genuine re-selection of
  // the last hand-picked sibling.
  const [prevCurrentViewUrn, setPrevCurrentViewUrn] = useState(currentViewUrn);
  if (prevCurrentViewUrn !== currentViewUrn) {
    setPrevCurrentViewUrn(currentViewUrn);
    setSelectedItemId(currentViewUrn);
  }

  const onClose = (itemClicked: SelectorItem | undefined): void => {
    const viewLink = itemClicked && data.links[itemClicked.id];

    if (viewLink && itemClicked && itemClicked.id !== selectedItemId) {
      setSelectedItemId(itemClicked.id);
      if (onSiblingSelected) {
        onSiblingSelected(viewLink);
      } else {
        events.onClose(viewUrn, viewLink);
      }
    }
  };

  const handleOnOpen = (): void => {
    events.onOpen(viewUrn);
  };

  const addThemingToBG = {
    backgroundImage: `url(${headerTheming})`,
    backgroundPosition: "right top",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
  };

  return (
    <div
      className={styles.container}
      style={headerTheming ? addThemingToBG : undefined}
      data-testid={SELECTORS.TEST_ID}
    >
      {data.items.length > 1 ? (
        <Selector
          title={data.title}
          isDesktop={window.innerWidth > 768}
          items={data.items}
          defaultId={data.defaultValue.id}
          onClose={onClose}
          onOpen={handleOnOpen}
        />
      ) : (
        <PageHeader title={data.defaultValue.label} withPadding={false} />
      )}
    </div>
  );
};

// Memoised: the host's fragment watches every sibling fixture's live scoreboard fields, so it
// re-renders at tick frequency while all of this card's props hold still (AGENTS.md — no compiler
// on web). Without this the drawer and its whole item list rebuild on every score update.
export default memo(EventSwitcherCard);
